import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * ForgotPassword — customer / driver enters their email, we POST it to
 * /api/auth/forgot-password. Backend always returns "silent success" so we
 * simply confirm the request was received, regardless of whether the email is
 * on file. In dev / no-Resend environments the response echoes back a
 * `reset_link` — when present we show a copyable link right there so the user
 * can proceed without waiting for email.
 */
export default function ForgotPassword() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      if (data?.reset_link) setDevLink(data.reset_link);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-bg text-white flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <button onClick={() => nav("/auth")} className="mb-6 inline-flex items-center gap-2 text-xs text-ink-textDim hover:text-white" data-testid="forgot-back">
          <ArrowLeft size={14} className="rtl:rotate-180" /> {t("auth.backToLogin", "Back to login")}
        </button>
        <div className="mb-6">
          <div className="w-14 h-14 rounded-2xl bg-electric-teal/15 border border-electric-teal/40 flex items-center justify-center mb-4">
            <Mail size={22} className="text-electric-teal" />
          </div>
          <h1 className="text-2xl font-black tracking-tight">{t("auth.forgotTitle", "Forgot your password?")}</h1>
          <p className="text-sm text-ink-textDim mt-1">
            {t("auth.forgotDesc", "Enter your registered email and we'll send you a one-time link to set a new password.")}
          </p>
        </div>
        {sent ? (
          <div className="bg-electric-teal/10 border border-electric-teal/40 rounded-2xl p-4 space-y-3" data-testid="forgot-sent">
            <div className="text-sm font-bold">
              {t("auth.forgotSent", "If that email is registered, a reset link has been sent.")}
            </div>
            <div className="text-xs text-ink-textDim">
              {t("auth.forgotHint", "Check your inbox (and spam). The link expires in 30 minutes.")}
            </div>
            {devLink && (
              <div className="bg-ink-bg border border-amber-gold/50 rounded-xl p-3 space-y-2" data-testid="forgot-dev-link">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-gold">
                  {t("auth.devLinkLabel", "Dev / no-email fallback link")}
                </div>
                <div className="font-mono text-[11px] break-all text-white/80">{devLink}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { navigator.clipboard.writeText(devLink); toast.success(t("common.copied", "Copied")); }}
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-xs font-bold"
                    data-testid="forgot-copy-link"
                  >
                    {t("common.copy", "Copy")}
                  </button>
                  <Link
                    to={devLink.replace(/^https?:\/\/[^/]+/, "")}
                    className="flex-1 bg-electric-teal text-obsidian text-center rounded-lg px-3 py-1.5 text-xs font-black"
                    data-testid="forgot-open-link"
                  >
                    {t("auth.openLink", "Open")}
                  </Link>
                </div>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPh", "you@example.com")}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-4 py-3 text-sm outline-none focus:border-electric-teal"
              data-testid="forgot-email"
              autoComplete="email"
              autoFocus
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-electric-teal text-obsidian rounded-full py-3.5 font-black flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="forgot-submit"
            >
              {busy && <Loader2 className="animate-spin" size={16} />}
              {t("auth.sendResetLink", "Send reset link")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
